import type { Schema } from "./api";

/** Launch-window rules that do not need React: which job the window continues, what locks it,
 * what the button says and whether the limit is enough. Runtime imports stay out so the module
 * runs under the plain Node test runner like svor.ts. */
export type Job = Schema["JobOut"];
export type Tier = "flex" | "default";
export type Mode = "all" | "norms";
type Cost = { usd_low: string | null; usd_high: string | null };

/** Money arrives as Decimal text. It is compared in whole 1e-8 dollars, never as a binary float. */
export function usdUnits(value: string | null | undefined): bigint | null {
  const match = /^\s*(\d+)(?:[.,](\d{1,8}))?\s*$/.exec(value ?? "");
  return match ? BigInt(match[1]) * 100_000_000n + BigInt((match[2] ?? "").padEnd(8, "0")) : null;
}

export const dollars = (value: string) => `$${Number(value).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function price(cost: Cost | undefined, unknown = "Нет подтверждённого тарифа") {
  if (!cost || cost.usd_low === null || cost.usd_high === null) return unknown;
  return cost.usd_low === cost.usd_high ? dollars(cost.usd_low) : `${dollars(cost.usd_low)}–${dollars(cost.usd_high)}`;
}

const count = (n: number) => new Intl.NumberFormat("ru-RU", { notation: "compact", maximumFractionDigits: 1 }).format(n);
export const interval = (low: number, high: number) => low === high ? count(low) : `${count(low)}–${count(high)}`;

export function duration(option: Pick<Schema["EstimateTier"], "duration_low_seconds" | "duration_high_seconds">) {
  if (option.duration_low_seconds === null || option.duration_high_seconds === null) return "Время пока не измерено";
  if (option.duration_high_seconds === 0) return "Без ожидания ответа модели";
  const hours = option.duration_high_seconds >= 7200;
  const divisor = hours ? 3600 : 60;
  const low = Math.max(hours ? .1 : 1, Math.floor(option.duration_low_seconds / divisor * (hours ? 10 : 1)) / (hours ? 10 : 1));
  const high = Math.max(low, Math.ceil(option.duration_high_seconds / divisor * (hours ? 10 : 1)) / (hours ? 10 : 1));
  return `Ориентир: ${interval(low, high)} ${hours ? "ч" : "мин"}`;
}

export type LaunchState = {
  active?: Job; previous?: Job; budgetJob?: Job; savedTier: Tier;
  /** Every unknown charge was acknowledged by the provider: continuing only fetches the paid answers. */
  fetchOnly: boolean;
  awaitingReconciliation: boolean;
  locked: boolean;
  /** The limit cannot be edited: another person set it, or a paid answer is still being fetched. */
  limitLocked: boolean;
};

export function launchState(jobs: Job[] | undefined, mode: Mode, busy: { processing: boolean; submitting: boolean }): LaunchState {
  const analyses = (jobs ?? []).filter(job => job.kind === "analyze");
  const active = analyses.find(job => ["queued", "running"].includes(job.status));
  const previous = analyses.find(job => (job.details.mode ?? "all") === mode && ["waiting_budget", "paused", "cancelled", "failed"].includes(job.status));
  const unresolved = analyses.find(job => !!(job.budget?.unknown_requests || job.budget?.pause_reason));
  const budgetJob = active ?? unresolved ?? previous;
  const budget = unresolved?.budget;
  const fetchOnly = !!budget && budget.unknown_requests > 0 && budget.recoverable_requests === budget.unknown_requests
    && unresolved === previous;
  const awaitingReconciliation = !!unresolved && !fetchOnly;
  const locked = busy.processing || !!active || busy.submitting || awaitingReconciliation;
  return {
    active, previous, budgetJob, fetchOnly, awaitingReconciliation, locked,
    savedTier: budgetJob?.details.service_tier === "default" ? "default" : "flex",
    limitLocked: locked || fetchOnly || (!!budgetJob?.budget && !budgetJob.budget.can_change_limit),
  };
}

export function launchLabel(state: LaunchState, view: { mode: Mode; allCached: boolean; processing: boolean; submitting: boolean }) {
  if (view.submitting) return "Отправляем…";
  if (state.active || view.processing) return "Обработка идёт";
  if (state.awaitingReconciliation) return "Ожидается сверка расходов";
  if (state.fetchOnly) return "Получить ответ и продолжить";
  if (state.previous) return view.mode === "norms" ? "Продолжить подбор норм" : "Продолжить анализ";
  if (view.mode === "norms") return "Подобрать нормы";
  return view.allCached ? "Использовать готовые результаты" : "Начать анализ";
}

export type BudgetWarning = { kind: "below_committed" | "below_reserve" | "below_estimate"; text: string };

/** The limit is a total, so what matters is what remains after the spent and reserved money. */
export function budgetWarning(input: {
  limit: string; spent?: string | null; reserved?: string | null;
  usdHigh?: string | null; minReserve?: string | null;
}): BudgetWarning | null {
  const limit = usdUnits(input.limit), spent = usdUnits(input.spent ?? "0"), reserved = usdUnits(input.reserved ?? "0");
  if (limit === null || spent === null || reserved === null) return null;
  const committed = spent + reserved, available = limit - committed;
  const usd = (units: bigint) => dollars(`${units / 100_000_000n}.${String(units % 100_000_000n).padStart(8, "0")}`);
  if (available < 0n) return { kind: "below_committed", text: `Предел ниже уже израсходованного и зарезервированного (${usd(committed)}). Продолжение с таким пределом будет отклонено.` };
  const reserve = usdUnits(input.minReserve), high = usdUnits(input.usdHigh);
  if (reserve !== null && available < reserve) return { kind: "below_reserve", text: `После расхода и резерва остаётся ${usd(available)}, а перед каждым запросом резервируется не меньше ${usd(reserve)}. Обработка остановится сразу, не отправив запрос; полученные результаты сохранятся.` };
  if (high !== null && available < high) return { kind: "below_estimate", text: `После расхода и резерва остаётся ${usd(available)} — это ниже верхней оценки оставшейся работы (${usd(high)}). Обработка может остановиться до завершения; полученные результаты сохранятся.` };
  return null;
}
